import * as v from 'valibot';
import { AIAssistantClientToolDefs, type AIAssistantGetViewImageOutput } from 'balus-common';
import type { CaptureViewTarget } from '../ViewImageCapture/CaptureViewTarget';
import { ViewImageCaptureLocator } from '../ViewImageCapture/ViewImageCaptureLocator';
import {
  CaptureBusyError,
  CaptureNotReadyError,
  CapturePayloadTooLargeError,
  CapturePermissionDeniedError,
  CaptureViewNameAmbiguousError,
  CaptureViewNotFoundError,
} from '../ViewImageCapture/ViewImageCaptureError';

export class GetViewImageToolError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GetViewImageToolError';
  }
}

type GetViewImageFallbacks = {
  editedViewIds: readonly string[];
  selectedViewIds: readonly string[];
};

// 対象未指定のときの既定対象。編集直後の確認が主な用途なので直前に編集したビューを優先し、
// 複数を編集していて絞れない場合だけ選択中のビューへ落とす。
function resolveFallbackTarget(fallbacks: GetViewImageFallbacks): CaptureViewTarget | undefined {
  const editedViewIds = [...new Set(fallbacks.editedViewIds)];
  if (editedViewIds.length === 1) return { viewId: editedViewIds[0] };

  const selectedViewIds = [...new Set(fallbacks.selectedViewIds)];
  return selectedViewIds.length === 1 ? { viewId: selectedViewIds[0] } : undefined;
}

function describeCaptureError(error: unknown): string {
  if (error instanceof CaptureViewNotFoundError) {
    const views = error.views.map((view) => `${view.id}（${view.name}）`).join(', ');
    return `ビュー"${error.specified}"が見つかりません。現在のビュー: ${views || 'なし'}`;
  }
  if (error instanceof CaptureViewNameAmbiguousError) {
    const candidates = error.candidates.map((view) => `${view.id}（${view.name}）`).join(', ');
    return `ビュー名"${error.viewName}"が複数のビューに一致します。viewIdで指定してください: ${candidates}`;
  }
  if (error instanceof CapturePermissionDeniedError) {
    return 'このグループのライセンスでは画像の書き出しが許可されていないため、ビュー画像を取得できません。';
  }
  if (error instanceof CaptureBusyError) {
    return '別の画像取得が進行中です。少し待ってから再度取得してください。';
  }
  if (error instanceof CaptureNotReadyError) {
    return 'ビューの描画が完了していないため画像を取得できません。少し待ってから再度取得してください。';
  }
  if (error instanceof CapturePayloadTooLargeError) {
    return '画像が大きすぎて取得できません。ビューの内容を減らしてから再度取得してください。';
  }
  return error instanceof Error ? error.message : '画像の取得に失敗しました。';
}

export async function runAIGetViewImageTool(
  input: unknown,
  fallbacks: GetViewImageFallbacks
): Promise<AIAssistantGetViewImageOutput> {
  const parsed = v.safeParse(AIAssistantClientToolDefs.getViewImage.inputSchema, input);
  if (!parsed.success) {
    throw new GetViewImageToolError(
      'ビュー画像取得の入力が不正です。viewIdまたはviewNameのどちらか一方だけを指定するか、どちらも省略してください。'
    );
  }

  const capture = ViewImageCaptureLocator.get();
  if (!capture) {
    throw new GetViewImageToolError(
      'ビュー画像を取得する準備ができていません。ビューの読み込み完了後に再度お試しください。'
    );
  }

  const target: CaptureViewTarget | undefined =
    'viewId' in parsed.output
      ? { viewId: parsed.output.viewId }
      : 'viewName' in parsed.output
        ? { viewName: parsed.output.viewName }
        : resolveFallbackTarget(fallbacks);
  if (!target) {
    throw new GetViewImageToolError('対象のビューを一意に決められません。viewIdまたはviewNameで指定してください。');
  }

  try {
    const { view, image } = await capture.captureViewImage(target);
    return {
      viewId: view.id,
      viewName: view.name,
      width: image.width,
      height: image.height,
      image: image.image,
    };
  } catch (error) {
    throw new GetViewImageToolError(describeCaptureError(error));
  }
}
